/**
 * Location Queries API Service
 *
 * Servicio interno de kanzi-location para todas las operaciones HTTP
 * relacionadas con locations, types y archivos.
 *
 * Este servicio encapsula KanziRestClientService y proporciona métodos
 * específicos para el dominio de locations, eliminando dependencias
 * externas de otras librerías.
 *
 * @internal Este servicio es para uso interno de kanzi-location únicamente
 */

import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { KanziRestClientService } from '@kanzi-apes/kanzi-rest-api';
import { EnvironmentService } from '@kanzi-apes/kanzi-utils';
import {
  LocationModel,
  LocationListModel,
  LocationFilterModel,
  LocationCreateModel,
  LocationTypeFilterModel,
  TypeLocationListModel,
  CountryModel,
  CountryListModel,
  CountryFilterModel,
  StateCountryModel,
  StateCountryListModel,
  StateCountryFilterModel,
  CityModel,
  CityListModel,
  CityFilterModel,
} from '@kanzi-apes/kanzi-models';

@Injectable({
  providedIn: 'root',
})
export class LocationQueriesApiService {
  private restClient = inject(KanziRestClientService);
  private envService = inject(EnvironmentService);

  private locationUrl = '';
  private locationTypesUrl = '';
  private locationFileUrl = '';
  private countriesUrl = '';
  private statesUrl = '';
  private citiesUrl = '';

  constructor() {
    if (this.envService.environment) {
      const kongApi = this.envService.environment.kongApi || '';
      const inventoryModule =
        this.envService.environment.apiModules.inventory || '';

      this.locationUrl = `${kongApi}${inventoryModule}locations/`;
      this.locationTypesUrl = `${kongApi}${inventoryModule}location-types/`;
      this.locationFileUrl = `${kongApi}${inventoryModule}load-locations-file/`;
      this.countriesUrl = `${kongApi}${inventoryModule}countries/`;
      this.statesUrl = `${kongApi}${inventoryModule}state-countries/`;
      this.citiesUrl = `${kongApi}${inventoryModule}cities/`;
    }
  }

  // ==================== LOCATIONS LIST ====================

  /**
   * Obtiene lista de locations con filtros opcionales
   *
   * @param filters - Filtros de búsqueda (page, page_size, search, type__name, etc.)
   * @returns Observable con lista paginada de locations
   *
   * @example
   * ```typescript
   * const filters = { page: 1, page_size: 50, type__name: 'WAREHOUSE' };
   * this.apiService.getLocationsList(filters).subscribe(data => {
   *   console.log('Locations:', data.results);
   *   console.log('Total:', data.count);
   * });
   * ```
   */
  getLocationsList(
    filters: LocationFilterModel | null
  ): Observable<LocationListModel> {
    // Add include_schedules parameter to filters
    const filtersWithSchedules = {
      ...filters,
      include_schedules: 'true'
    };
    
    return this.restClient.get<LocationListModel>(
      this.locationUrl,
      filtersWithSchedules,
      'Error al obtener las locaciones'
    );
  }

  // ==================== LOCATION DETAIL ====================

  /**
   * Obtiene el detalle de una location específica por ID
   *
   * @param id - ID de la location
   * @returns Observable con el detalle de la location
   *
   * @example
   * ```typescript
   * this.apiService.getLocationDetail(123).subscribe(location => {
   *   console.log('Location:', location.name);
   * });
   * ```
   */
  getLocationDetail(id: number): Observable<LocationModel> {
    const params = { include_schedules: 'true' };
    return this.restClient.get<LocationModel>(
      `${this.locationUrl}${id}/`,
      params,
      `Error al obtener la locación ${id}`
    );
  }

  // ==================== CREATE LOCATION ====================

  /**
   * Crea una nueva location
   *
   * @param location - Datos de la location a crear
   * @returns Observable con la location creada
   *
   * @example
   * ```typescript
   * const newLocation: LocationCreateModel = {
   *   name: 'Warehouse A1',
   *   external_id: 'WH-A1',
   *   type: 1, // ID del tipo WAREHOUSE
   *   customer: 5
   * };
   *
   * this.apiService.createLocation(newLocation).subscribe(created => {
   *   console.log('Location creada:', created.id);
   * });
   * ```
   */
  createLocation(location: LocationCreateModel): Observable<LocationModel> {
    return this.restClient.post<LocationModel>(
      this.locationUrl,
      location,
      'Error al crear la locación'
    );
  }

  // ==================== UPDATE LOCATION ====================

  /**
   * Actualiza una location existente
   *
   * NOTA: Usa PATCH
   *
   * @param location - Datos de la location a actualizar (debe incluir id)
   * @returns Observable con la location actualizada
   *
   * @example
   * ```typescript
   * const updatedLocation: LocationCreateModel = {
   *   id: 123,
   *   name: 'Warehouse A1 - Updated',
   *   external_id: 'WH-A1',
   *   type: 1,
   *   customer: 5
   * };
   *
   * this.apiService.updateLocation(updatedLocation).subscribe(updated => {
   *   console.log('Location actualizada');
   * });
   * ```
   */
  updateLocation(location: LocationCreateModel): Observable<LocationModel> {
    if (!location.id) {
      throw new Error('Location ID is required for update');
    }

    return this.restClient.patch<LocationModel>(
      `${this.locationUrl}${location.id}/`,
      location,
      null,
      `Error al actualizar la locación ${location.id}`
    );
  }

  // ==================== DELETE LOCATION ====================

  /**
   * Elimina una location por ID
   *
   * @param id - ID de la location a eliminar
   * @returns Observable con la location eliminada
   *
   * @example
   * ```typescript
   * this.apiService.deleteLocation(123).subscribe(() => {
   *   console.log('Location eliminada');
   * });
   * ```
   */
  deleteLocation(id: number): Observable<LocationModel> {
    return this.restClient.delete<LocationModel>(
      `${this.locationUrl}${id}/`,
      `Error al eliminar la locación ${id}`
    );
  }

  // ==================== UPLOAD FILE ====================

  /**
   * Carga masiva de locations desde archivo
   *
   * @param formData - FormData con el archivo y metadata
   * @returns Observable con el resultado de la carga
   *
   * @example
   * ```typescript
   * const formData = new FormData();
   * formData.append('file', fileBlob, 'locations.xlsx');
   * formData.append('type', 'WAREHOUSE');
   * formData.append('customer', '5');
   *
   * this.apiService.uploadLocationsFile(formData).subscribe(result => {
   *   console.log('Locations cargadas:', result);
   * });
   * ```
   */
  uploadLocationsFile(formData: FormData): Observable<any> {
    return this.restClient.postFormData(
      this.locationFileUrl,
      formData,
      'Error al cargar las locaciones desde archivo'
    );
  }

  // ==================== LOCATION TYPES ====================

  /**
   * Obtiene la lista de tipos de locations
   *
   * @param filters - Filtros opcionales para tipos
   * @returns Observable con lista de tipos
   *
   * @example
   * ```typescript
   * this.apiService.getLocationTypes(null).subscribe(types => {
   *   console.log('Tipos:', types.results);
   * });
   *
   * // Con filtro:
   * this.apiService.getLocationTypes({ name: 'WAREHOUSE' }).subscribe(types => {
   *   console.log('Tipo WAREHOUSE:', types.results[0]);
   * });
   * ```
   */
  getLocationTypes(
    filters: LocationTypeFilterModel | null
  ): Observable<TypeLocationListModel> {
    return this.restClient.get<TypeLocationListModel>(
      this.locationTypesUrl,
      filters,
      'Error al obtener los tipos de locación'
    );
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Obtiene la URL base para locations
   * Útil para construir URLs personalizadas si es necesario
   */
  getLocationUrl(): string {
    return this.locationUrl;
  }

  /**
   * Obtiene la URL base para location types
   */
  getLocationTypesUrl(): string {
    return this.locationTypesUrl;
  }

  /**
   * Obtiene la URL para carga de archivos
   */
  getLocationFileUrl(): string {
    return this.locationFileUrl;
  }

  // ==================== GEOGRAPHIC DATA ====================

  /**
   * Obtiene la lista de países
   *
   * @param filters - Filtros opcionales para países
   * @returns Observable con lista de países
   *
   * @example
   * ```typescript
   * this.apiService.getCountries(null).subscribe(countries => {
   *   console.log('Países:', countries.results);
   * });
   * ```
   */
  getCountries(
    filters: CountryFilterModel | null
  ): Observable<CountryListModel> {
    return this.restClient.get<CountryListModel>(
      this.countriesUrl,
      filters,
      'Error al obtener los países'
    );
  }

  /**
   * Obtiene la lista de estados/provincias
   *
   * @param filters - Filtros opcionales (típicamente country_id)
   * @returns Observable con lista de estados
   *
   * @example
   * ```typescript
   * this.apiService.getStates({ country_id: 1 }).subscribe(states => {
   *   console.log('Estados:', states.results);
   * });
   * ```
   */
  getStates(
    filters: StateCountryFilterModel | null
  ): Observable<StateCountryListModel> {
    return this.restClient.get<StateCountryListModel>(
      this.statesUrl,
      filters,
      'Error al obtener los estados'
    );
  }

  /**
   * Obtiene la lista de ciudades
   *
   * @param filters - Filtros opcionales (típicamente state_id)
   * @returns Observable con lista de ciudades
   *
   * @example
   * ```typescript
   * this.apiService.getCities({ state_id: 1 }).subscribe(cities => {
   *   console.log('Ciudades:', cities.results);
   * });
   * ```
   */
  getCities(
    filters: CityFilterModel | null
  ): Observable<CityListModel> {
    return this.restClient.get<CityListModel>(
      this.citiesUrl,
      filters,
      'Error al obtener las ciudades'
    );
  }
}
